import { useCallback, useEffect, useState } from "react";
import { useApi } from "../lib/apiClient";
import { useAppAuth } from "../contexts/AuthContext";
import {
  isExpired,
  useExclusions,
  type ExclusionsApi,
} from "../components/exclusions";
import { EmployeePicker } from "../components/EmployeePicker";
import type { AllowlistEvent, SyncExclusion } from "../lib/types";
import { fmtSgtDateTime, sgtToday } from "../lib/datetime";

interface GuestUser {
  id: string;
  email: string;
  role: "user" | "admin" | null;
  banned: boolean;
}
interface Invite {
  email: string;
  role: "user" | "admin";
  pending: true;
}
interface UsersResponse {
  users: GuestUser[];
  invites: Invite[];
}

export default function SettingsPage() {
  const { request } = useApi();
  const { appUser } = useAppAuth();
  const [data, setData] = useState<UsersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"user" | "admin">("user");
  const [busy, setBusy] = useState(false);
  const ex = useExclusions();

  const load = useCallback(async () => {
    try {
      setData(await request<UsersResponse>("/api/admin/users"));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      // Role changes move people on/off the always-exempt admin list too.
      await Promise.all([load(), ex.reload()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    void act(async () => {
      await request("/api/admin/invite", {
        method: "POST",
        body: JSON.stringify({ email, role }),
      });
      setEmail("");
      setRole("user");
    });
  };

  return (
    <div className="page">
      <h1>Settings — Guest list</h1>
      {error && <p className="error">{error}</p>}

      <form onSubmit={invite} className="row">
        <input
          type="email"
          placeholder="email@leychoon.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as "user" | "admin")}
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button type="submit" disabled={busy}>
          Invite
        </button>
      </form>

      <h2>Members</h2>
      <table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data?.users.map((u) => {
            const isSelf = u.email === appUser?.email;
            return (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.role ?? "—"}</td>
                <td>{u.banned ? "deactivated" : "active"}</td>
                <td className="actions">
                  {!isSelf && (
                    <>
                      <button
                        disabled={busy}
                        onClick={() =>
                          void act(() =>
                            request(`/api/admin/users/${u.id}/role`, {
                              method: "POST",
                              body: JSON.stringify({
                                role: u.role === "admin" ? "user" : "admin",
                              }),
                            }),
                          )
                        }
                      >
                        {u.role === "admin" ? "Make user" : "Make admin"}
                      </button>
                      {u.banned ? (
                        <button
                          disabled={busy}
                          onClick={() =>
                            void act(() =>
                              request(`/api/admin/users/${u.id}/reactivate`, {
                                method: "POST",
                              }),
                            )
                          }
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          disabled={busy}
                          onClick={() =>
                            void act(() =>
                              request(`/api/admin/users/${u.id}/deactivate`, {
                                method: "POST",
                              }),
                            )
                          }
                        >
                          Deactivate
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {data && data.invites.length > 0 && (
        <>
          <h2>Pending invites</h2>
          <ul>
            {data.invites.map((i) => (
              <li key={i.email}>
                {i.email} — {i.role}
              </li>
            ))}
          </ul>
        </>
      )}

      <AllowlistSection ex={ex} />

      <ExemptionHistory />
    </div>
  );
}

/** One allowlist entry: shows its link (if any) and lets an admin change it. */
function AllowlistRow({ row, ex }: { row: SyncExclusion; ex: ExclusionsApi }) {
  const [editing, setEditing] = useState(false);
  const [dating, setDating] = useState(false);
  const [expiry, setExpiry] = useState(row.expiresAt ?? "");
  const expired = isExpired(row);

  const setLink = (employeeNo: string | null) => {
    void ex.add(row.canonicalEmail, { employeeNo }).then(
      () => setEditing(false),
      () => {
        /* error surfaced above the table */
      },
    );
  };

  const saveExpiry = (value: string | null) => {
    void ex.add(row.canonicalEmail, { expiresAt: value }).then(
      () => setDating(false),
      () => {
        /* error surfaced above the table */
      },
    );
  };

  return (
    <tr>
      <td>{row.canonicalEmail}</td>
      <td>
        {editing ? (
          <EmployeePicker
            value={row.linkedEmployeeNo}
            onChange={setLink}
            load={ex.loadLinkable}
            disabled={ex.busy}
          />
        ) : row.linkedEmployeeNo ? (
          <>
            #{row.linkedEmployeeNo}{" "}
            <span className="muted">(follows their status)</span>
          </>
        ) : (
          <span className="muted">never lock</span>
        )}
      </td>
      <td>{row.note ?? "—"}</td>
      <td>
        {dating ? (
          <span className="picker">
            <input
              type="date"
              value={expiry}
              min={sgtToday()}
              onChange={(e) => setExpiry(e.target.value)}
            />
            <button
              className="btn-inline"
              disabled={ex.busy || !expiry}
              onClick={() => saveExpiry(expiry)}
            >
              Save
            </button>
            <button className="btn-inline" onClick={() => setDating(false)}>
              Cancel
            </button>
          </span>
        ) : row.expiresAt ? (
          <span className={expired ? "error" : undefined}>
            {row.expiresAt} {expired ? "(expired — no longer exempt)" : ""}
          </span>
        ) : (
          <span className="muted">no end date</span>
        )}
      </td>
      <td>{fmtSgtDateTime(row.createdAt)}</td>
      <td className="actions">
        {editing ? (
          <button disabled={ex.busy} onClick={() => setEditing(false)}>
            Cancel
          </button>
        ) : (
          <button disabled={ex.busy} onClick={() => setEditing(true)}>
            {row.linkedEmployeeNo ? "Change link" : "Link"}
          </button>
        )}
        {row.linkedEmployeeNo && !editing && (
          <button disabled={ex.busy} onClick={() => setLink(null)}>
            Unlink
          </button>
        )}
        {!dating && (
          <button disabled={ex.busy} onClick={() => setDating(true)}>
            {row.expiresAt ? "Change expiry" : "Set expiry"}
          </button>
        )}
        {row.expiresAt && !dating && (
          <button disabled={ex.busy} onClick={() => saveExpiry(null)}>
            Clear expiry
          </button>
        )}
        <button
          disabled={ex.busy}
          onClick={() => {
            void ex.remove(row.canonicalEmail).catch(() => {
              /* error surfaced above the table */
            });
          }}
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

/**
 * Never-lock allowlist management. Distinct from the guest list above: this
 * exempts an identity from the *org-wide Clerk lock* the sync performs, it does
 * not grant anyone access to this tool.
 */
function AllowlistSection({ ex }: { ex: ExclusionsApi }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [employeeNo, setEmployeeNo] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [linking, setLinking] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void ex.add(email, { note, employeeNo, expiresAt: expiresAt || null }).then(
      () => {
        setEmail("");
        setNote("");
        setEmployeeNo(null);
        setExpiresAt("");
        setLinking(false);
      },
      () => {
        /* error surfaced below */
      },
    );
  };

  const rows = ex.data?.exclusions ?? [];
  const envRows = ex.data?.envExclusions ?? [];
  const adminRows = ex.data?.adminExclusions ?? [];

  return (
    <>
      <h2>Never-lock allowlist</h2>
      <p className="muted">
        Emails the sync must never lock, even when HR says they have left. Use it
        for shared mailboxes, contractors and service accounts that have no HR
        record. Matched on the base address, so <code>staff+tag@</code> is
        covered by <code>staff@</code>.
      </p>
      <p className="muted">
        Adding someone here only affects <strong>future runs</strong> — the sync
        never unlocks, so anyone already locked in Clerk still needs a manual
        re-grant.
      </p>
      <p className="muted">
        <strong>Set an expiry</strong> whenever the reason has an end date. An
        exemption with no end date outlives whatever justified it; an expired one
        stops applying on its own the day after the date shown, and the entry
        stays visible so you can see it lapsed.
      </p>
      <p className="muted">
        <strong>Linking to an employee</strong> is the better option when the
        login belongs to a real member of staff HR has no email address for. A
        linked entry is <em>not</em> a permanent exemption: the sync judges the
        login by that employee's employment status, so access ends by itself on
        their last working day. Only still-employed, email-less staff can be
        linked.
      </p>
      {ex.error && <p className="error">{ex.error}</p>}

      <form onSubmit={submit} className="row">
        <input
          type="email"
          placeholder="email@leychoon.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="reason (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <label className="picker" title="Optional: the entry stops applying after this SGT date">
          <span className="muted">until</span>
          <input
            type="date"
            value={expiresAt}
            min={sgtToday()}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </label>
        {linking ? (
          <EmployeePicker
            value={employeeNo}
            onChange={setEmployeeNo}
            load={ex.loadLinkable}
            disabled={ex.busy}
          />
        ) : (
          <button
            type="button"
            className="btn-inline"
            onClick={() => setLinking(true)}
          >
            Link to an employee…
          </button>
        )}
        <button type="submit" disabled={ex.busy}>
          {employeeNo ? `Add & link #${employeeNo}` : "Add to allowlist"}
        </button>
      </form>

      {rows.length === 0 ? (
        <p className="muted">No emails added yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Linked employee</th>
              <th>Reason</th>
              <th>Expires</th>
              <th>Added</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <AllowlistRow key={r.canonicalEmail} row={r} ex={ex} />
            ))}
          </tbody>
        </table>
      )}

      {(adminRows.length > 0 || envRows.length > 0) && (
        <details className="detail-block">
          <summary>
            Also exempt automatically ({adminRows.length + envRows.length})
          </summary>
          {adminRows.length > 0 && (
            <>
              <p className="muted">
                App admins — always exempt, so the sync can never lock the people
                who operate it:
              </p>
              <ul className="pills">
                {adminRows.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </>
          )}
          {envRows.length > 0 && (
            <>
              <p className="muted">
                From <code>SYNC_EXCLUDE_EMAILS</code> — set in the deployment
                environment, changed by a redeploy rather than here:
              </p>
              <ul className="pills">
                {envRows.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </>
          )}
        </details>
      )}
    </>
  );
}

const ACTION_LABEL: Record<AllowlistEvent["action"], string> = {
  allowlist_add: "added to allowlist",
  allowlist_update: "allowlist entry changed",
  allowlist_remove: "removed from allowlist",
  admin_granted: "made an app admin",
  admin_revoked: "admin removed",
  user_deactivated: "deactivated",
  user_reactivated: "reactivated",
};

/**
 * The append-only record of every change to who is exempt from locking.
 *
 * The table above only ever shows the current state — a removed entry leaves no
 * trace there at all. This is the part that survives: who changed what, when,
 * including things that no longer exist.
 */
function ExemptionHistory() {
  const { request } = useApi();
  const [events, setEvents] = useState<AllowlistEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    request<{ events: AllowlistEvent[] }>("/api/admin/allowlist-events")
      .then((d) => setEvents(d.events))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [request]);

  return (
    <>
      <h2>Exemption history</h2>
      <p className="muted">
        Every change to who the sync will never lock — allowlist edits and admin
        grants alike, including entries that have since been deleted. This log is
        append-only and is never pruned.
      </p>
      {error && <p className="error">{error}</p>}
      {!events && !error && <p className="muted">Loading…</p>}
      {events && events.length === 0 && (
        <p className="muted">No changes recorded yet.</p>
      )}
      {events && events.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>What</th>
              <th>Subject</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td>{fmtSgtDateTime(e.at)}</td>
                <td>{e.actorEmail ?? "system"}</td>
                <td>{ACTION_LABEL[e.action] ?? e.action}</td>
                <td>{e.subject}</td>
                <td>
                  {[
                    e.detail,
                    e.linkedEmployeeNo ? `linked #${e.linkedEmployeeNo}` : null,
                    e.expiresAt ? `expires ${e.expiresAt}` : null,
                    e.note ? `"${e.note}"` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
